import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, interval, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { BloqueoModulo } from '../object/bloqueo-vista.Interface';
import { InputParamsBloqueoVista } from '../object/input-params-bloqueo-vista.Interface';
import { StorageVariable } from '../object/local-storage-variable.enum';
import { ConsoleService } from './console.service';
import { SecurityService } from './security.service';

@Injectable({
  providedIn: 'root',
})
export class BloqueoModuloService {
  private API_URL = environment.API_REST_URL;
  private ENDPOINT_BASE = `${this.API_URL}/bloqueo_modulo`; // Updated endpoint base

  private rutUsuario: number;
  private codigoModuloBloqueado: number | null = null;

  private estadoBloqueoActual = new BehaviorSubject<BloqueoModulo | null>(null);
  public estadoBloqueo$ = this.estadoBloqueoActual.asObservable();

  private estadoAdquirirBloqueo = new BehaviorSubject<'loading' | 'success' | 'error' | 'pending'>(null);
  public estadoAdquirirBloqueo$ = this.estadoAdquirirBloqueo.asObservable();

  private actualizacionSubscription: Subscription;
  private stopTimer$ = new Subject<void>();

  private log: any;

  constructor(
    private http: HttpClient,
    private clog: ConsoleService,
    private securityService: SecurityService,
  ) {
    this.log = this.clog.log('BLOQUEO-MODULO-SERVICE');
    this.obtenerRutUsuario();
  }

  get bloqueoActualValue(): BloqueoModulo | null {
    return this.estadoBloqueoActual.getValue();
  }

  getRutUsuario(): number {
    return this.rutUsuario;
  }

  getCodigoModuloBloqueado(): number | null {
    return this.codigoModuloBloqueado;
  }

  obtenerRutUsuario(): void {
    const rutUsuarioString = localStorage.getItem(StorageVariable.RUT_USUARIO);
    this.rutUsuario = rutUsuarioString ? Number(rutUsuarioString) : 0;
  }

  obtenerTodosLosBloqueos(inputParams: {
    codigoModulo: number,
    rutUsuario: number,
  }): Observable<BloqueoModulo[]> {
    return this.http.get<BloqueoModulo[]>(`${this.ENDPOINT_BASE}/obtenerTodos/${inputParams.codigoModulo}/${inputParams.rutUsuario}`)
  }

  verificarBloqueoModulo(codigoModulo: number): Observable<BloqueoModulo> {
    return this.http.get<BloqueoModulo>(`${this.ENDPOINT_BASE}/verificarBloqueoModulo/${codigoModulo}`)
  }

  verificarBloqueoActualActivo(inputParams: {
    rutUsuario: number,
    codigoModulo: number,
  }): Observable<BloqueoModulo> {
    const rutUsuario = inputParams.rutUsuario;
    const codigoModulo = inputParams.codigoModulo;
    const tokenRefresco = this.obtenerTokenRefresco();

    const params: InputParamsBloqueoVista = {
      rut_usuario: rutUsuario,
      codigo_modulo: codigoModulo,
      token_refresco: tokenRefresco,
    }
    return this.http.post<BloqueoModulo>(`${this.ENDPOINT_BASE}/verificarBloqueoActualActivo`, params);
  }

  adquirirBloqueo(codigoModulo: number): Observable<BloqueoModulo> {
    this.estadoAdquirirBloqueo.next('loading');

    const tokenRefresco = this.obtenerTokenRefresco();
    if (!this.rutUsuario || !tokenRefresco) {
      const errorResponse: BloqueoModulo = {
        exito: false,
        mensajeError: 'El RUT del usuario o el token de refresco no están disponibles. No se puede adquirir el bloqueo.',
        codigo_modulo: codigoModulo,
        rut_usuario: this.rutUsuario,
        token_refresco: tokenRefresco,
        fecha_expiracion: new Date().toISOString(),
        fecha_ultima_actividad: new Date().toISOString(),
      };
      this.estadoAdquirirBloqueo.next('error');
      this.estadoBloqueoActual.next(errorResponse);
      return of(errorResponse);
    }
    const params: InputParamsBloqueoVista = {
      rut_usuario: this.rutUsuario,
      codigo_modulo: codigoModulo,
      token_refresco: tokenRefresco,
    };

    return this.http.post(`${this.ENDPOINT_BASE}/adquirir`, params).pipe(
      tap((bloqueo: BloqueoModulo) => {
        if (bloqueo.exito) {
          this.estadoAdquirirBloqueo.next('success');
          this.log('ADQUIRIR BLOQUEO', 'EXITO', bloqueo);
          this.codigoModuloBloqueado = codigoModulo;
          this.iniciarActualizacionPeriodica();
        }else{
          this.estadoAdquirirBloqueo.next('error');
          // SI NO ES EXITOSO, SE DEBE VERIFICAR SI EL MODULO ESTA BLOQUEADO LOCALMENTE
          if(this.codigoModuloBloqueado === codigoModulo){
            this.log('ADQUIRIR BLOQUEO', 'NO EXITO', bloqueo);
            this.forzarLiberacionLocal(bloqueo.mensajeError, codigoModulo);
          }
        }
        this.estadoBloqueoActual.next(bloqueo);
      }),
      catchError((error: HttpErrorResponse) => {
        this.log('ADQUIRIR BLOQUEO', 'ERROR', error);
        const errorResponse: BloqueoModulo = {
          exito: false,
          mensajeError: `Error de comunicación al adquirir bloqueo: ${error.message}`,
          codigo_modulo: codigoModulo,
          rut_usuario: this.rutUsuario,
          token_refresco: tokenRefresco,
          fecha_expiracion: new Date().toISOString(),
          fecha_ultima_actividad: new Date().toISOString(),
        };
        this.estadoAdquirirBloqueo.next('error');
        this.estadoBloqueoActual.next(errorResponse);
        return of(errorResponse);
      })
    );
  }

  liberarBloqueo(codigoModulo?: number, rutUsuario?: number): Observable<BloqueoModulo> {
    this.log('LIBERANDO BLOQUEO', codigoModulo, this.codigoModuloBloqueado);
    const moduloALiberar = codigoModulo || this.codigoModuloBloqueado;
    const rutUsuarioALiberar = rutUsuario || this.rutUsuario;
    const tokenRefresco = this.obtenerTokenRefresco();

    if (!moduloALiberar || !rutUsuarioALiberar || !tokenRefresco) {
      const response: BloqueoModulo = {
        exito: false,
        mensajeError: 'Error al liberar bloqueo. No existe módulo, usuario o token de refresco.',
        codigo_modulo: moduloALiberar || 0,
        rut_usuario: rutUsuarioALiberar || 0,
        token_refresco: tokenRefresco,
        fecha_expiracion: new Date().toISOString(),
        fecha_ultima_actividad: new Date().toISOString(),
      }
      // this.estadoBloqueoActual.next(response);
      this.log('LIBERANDO BLOQUEO', 'NO EXITO', response);
      return of(response);
    }

    const params: InputParamsBloqueoVista = {
      rut_usuario: rutUsuarioALiberar,
      codigo_modulo: moduloALiberar,
      token_refresco: tokenRefresco,
    }
    
    return this.http.post(`${this.ENDPOINT_BASE}/liberar`, params).pipe(
      tap((response: BloqueoModulo) => {
        this.log('LIBERANDO BLOQUEO', 'EXITO', response);
        if (response.exito && this.codigoModuloBloqueado === moduloALiberar) {
          this.forzarLiberacionLocal(undefined, moduloALiberar, true);
        }else if (!response.exito  && this.codigoModuloBloqueado === moduloALiberar) {
          this.estadoBloqueoActual.next(response);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        this.log('LIBERANDO BLOQUEO', 'ERROR', error);
        const errorResponse: BloqueoModulo = {
          exito: false,
          mensajeError: `Error de comunicación al liberar bloqueo: ${error.message}`,
          codigo_modulo: moduloALiberar,
          rut_usuario: rutUsuarioALiberar,
          token_refresco: tokenRefresco,
          fecha_expiracion: new Date().toISOString(),
          fecha_ultima_actividad: new Date().toISOString(),
        }
        if (this.codigoModuloBloqueado === moduloALiberar) {
          this.estadoBloqueoActual.next(errorResponse);
        }
        return of(errorResponse);
      })
    )
  }

  forzarLiberacion() {
    this.forzarLiberacionLocal(undefined, this.codigoModuloBloqueado, true);
  }

  liberarTodosLosBloqueosDelUsuarioActual(sendBeacon: boolean = false): Observable<BloqueoModulo> {
    return this.liberarTodosLosBloqueosPorSesion(sendBeacon);
  }

  // METODO PARA INTENTAR LIBERAR EL BLOQUEO AL SALIR DE LA APLICACION
  liberarBloqueoAlSalirDelNavegador(codigoModulo: number): BloqueoModulo | null {
    const tokenRefresco = this.obtenerTokenRefresco();
    if (!this.rutUsuario || !codigoModulo || !tokenRefresco) return null;

    const params: InputParamsBloqueoVista = {
      rut_usuario: this.rutUsuario,
      codigo_modulo: codigoModulo,
      token_refresco: tokenRefresco,
    };

    // SE OCUPA SEND BEACON SOLO SI EXISTE EN EL NAVEGADOR
    if (navigator && typeof navigator.sendBeacon === 'function') {
      this.intentarSendBeacon(params);
    }else{
      // SI SENDBEACON NO EXISTE EN LA API DEL NAVEGADOR, 
      // SE USA XMLHttpRequest SINCRONO, 
      // (NO ES RECOMENDABLE POR QUE ES PROBLEMATICO Y NO GARANTIZAN LA LIBERACION DEL BLOQUEO)
      // EL EVENTO beforeunload NO PERMITE HACER LLAMADAS ASINCRONAS
      // SE TIENE POCO TIEMPO PARA EJECUTAR EL CÓDIGO ANTES QUE SE CIERRE EL NAVEGADOR
      try {
        const request = new XMLHttpRequest();
        request.open('POST', `${this.ENDPOINT_BASE}/liberarAlCerrarNavegador`, false); // Síncrono
        request.setRequestHeader('Content-Type', 'text/plain; charset=UTF-8');
        request.send(JSON.stringify(params));
  
        if (request.status >= 200 && request.status < 300) {
          const response = JSON.parse(request.responseText) as BloqueoModulo;
          if (response.exito && this.codigoModuloBloqueado === codigoModulo) {
            this.forzarLiberacionLocal(undefined, codigoModulo, true);
          }
          return response;
        }
      } catch (e) {
        console.error('Error en liberación síncrona:', e);
      }
    }
    return null;
  }

  private intentarSendBeacon(params: InputParamsBloqueoVista): void {
    const ok = this.enviarConSendBeacon(`${this.ENDPOINT_BASE}/liberarAlCerrarNavegador`, params);
    // Como sendBeacon es fire-and-forget, no hay respuesta sincrónica.
    if (ok && this.codigoModuloBloqueado === params.codigo_modulo) {
      this.forzarLiberacionLocal(undefined, params.codigo_modulo, true);
    }
  }

  private enviarConSendBeacon(url: string, payload: InputParamsBloqueoVista): boolean {
    try {
      if (!("sendBeacon" in navigator)) {
        // Feature no disponible
        return false;
      }
      const blob = new Blob([JSON.stringify(payload)], {
        type: 'text/plain; charset=UTF-8',
      });
      return navigator.sendBeacon(url, blob);
    } catch (error) {
      console.error('Error al invocar sendBeacon:', error);
      return false;
    }
  }

  private actualizarBloqueo(): Observable<BloqueoModulo> {
    this.log('ACTUALIZANDO BLOQUEO...');
    const tokenRefresco = this.obtenerTokenRefresco();
    if (!this.codigoModuloBloqueado || !this.rutUsuario || !tokenRefresco) {
      this.forzarLiberacionLocal('Error al actualizar bloqueo. No existe módulo, usuario o token de refresco.', this.codigoModuloBloqueado);
      return of({
        exito: false,
        mensajeError: 'Error al actualizar bloqueo. No existe módulo, usuario o token de refresco.',
        codigo_modulo: this.codigoModuloBloqueado || 0,
        rut_usuario: this.rutUsuario || 0,
        token_refresco: tokenRefresco,
        fecha_expiracion: new Date().toISOString(),
        fecha_ultima_actividad: new Date().toISOString(),
      } as BloqueoModulo)
    }

    const params: InputParamsBloqueoVista = {
      rut_usuario: this.rutUsuario,
      codigo_modulo: this.codigoModuloBloqueado,
      token_refresco: this.obtenerTokenRefresco(),
    }

    return this.http.post<BloqueoModulo>(`${this.ENDPOINT_BASE}/actualizar`, params).pipe(
      tap((response: BloqueoModulo) => {
        this.log('BLOQUEO ACTUALIZADO', response);
        if (!response.exito) {
          this.log('BLOQUEO ACTUALIZADO', 'NO EXITO', response);
          this.forzarLiberacionLocal(response.mensajeError, this.codigoModuloBloqueado);
        }
        this.log('BLOQUEO ACTUALIZADO', 'EXITO', response);
        this.estadoBloqueoActual.next(response);
      }),
      catchError((error: HttpErrorResponse) => {
        this.log('BLOQUEO ACTUALIZADO', 'ERROR', error);
        this.forzarLiberacionLocal(`Error de comunicación al actualizar bloqueo: ${error.message}`, this.codigoModuloBloqueado);
        const errorResponse: BloqueoModulo = {
          exito: false,
          mensajeError: `Error de comunicación al actualizar bloqueo: ${error.message}`,
          codigo_modulo: this.codigoModuloBloqueado || 0,
          rut_usuario: this.rutUsuario || 0,
          token_refresco: tokenRefresco,
          fecha_expiracion: new Date().toISOString(),
          fecha_ultima_actividad: new Date().toISOString(),
        };
        this.estadoBloqueoActual.next(errorResponse);
        return of(errorResponse);
      })
    )
  }

  private liberarTodosLosBloqueosPorSesion(sendBeacon: boolean = false): Observable<BloqueoModulo> {
    this.log('LIBERANDO TODOS LOS BLOQUEOS');
    const tokenRefresco = this.obtenerTokenRefresco();
    if (!this.rutUsuario || !tokenRefresco) {
      return of({
        exito: false,
        mensajeError: 'El RUT del usuario o el token de refresco no están disponibles. No se pueden liberar los bloqueos.',
        codigo_modulo: 0,
        rut_usuario: 0,
        token_refresco: tokenRefresco,
        fecha_expiracion: new Date().toISOString(),
        fecha_ultima_actividad: new Date().toISOString(),
      } as BloqueoModulo);
    }

    const params: InputParamsBloqueoVista = {
      rut_usuario: this.rutUsuario,
      token_refresco: tokenRefresco,
      codigo_modulo: 0,
    };

    if(sendBeacon){
      const resultado = this.enviarConSendBeacon(`${this.ENDPOINT_BASE}/liberarTodosPorSesionSendBeacon`, params);
      return of({
        exito: resultado,
        mensajeError: resultado ? '' : 'Error al liberar todos los bloqueos por sesión',
        codigo_modulo: 0,
        rut_usuario: this.rutUsuario || 0,
        token_refresco: tokenRefresco,
        fecha_expiracion: new Date().toISOString(),
        fecha_ultima_actividad: new Date().toISOString(),
      } as BloqueoModulo);
    }

    return this.http.post<BloqueoModulo>(`${this.ENDPOINT_BASE}/liberarTodosPorSesion`, params).pipe(
      tap((response: BloqueoModulo) => {
        this.log('LIBERANDO TODOS LOS BLOQUEOS DE LA SESION ACTUAL', response);
        if (response.exito) {
          this.forzarLiberacionLocal(undefined, this.codigoModuloBloqueado, true);
        }
        // this.estadoBloqueoActual.next(response);
      }),
      catchError((error: HttpErrorResponse) => {
        this.log('LIBERANDO TODOS LOS BLOQUEOS DE LA SESION ACTUAL', 'ERROR', error);
        const errorResponse: BloqueoModulo = {
          exito: false,
          mensajeError: `Error de comunicación al liberar bloqueo: ${error.message}`,
          codigo_modulo: 0,
          rut_usuario: this.rutUsuario,
          token_refresco: tokenRefresco,
          fecha_expiracion: new Date().toISOString(),
          fecha_ultima_actividad: new Date().toISOString(),
        }
        // this.estadoBloqueoActual.next(errorResponse);
        return of(errorResponse);
      })
    )
  }

  private iniciarActualizacionPeriodica() {
    this.detenerActualizacionPeriodica();
    this.log('INICIAR ACTUALIZACION PERIODICA');
    this.actualizacionSubscription = interval(60 * 1000)
      .pipe(
        takeUntil(this.stopTimer$),
        filter(() => this.codigoModuloBloqueado !== null && this.rutUsuario !== null),
        switchMap(() => {
          this.log('ACTUALIZANDO BLOQUEO...');
          return this.actualizarBloqueo()
        })
      ).subscribe();
  }

  private detenerActualizacionPeriodica(): void {
    this.log('DETENIENDO ACTUALIZACION PERIODICA');
    this.stopTimer$.next();
    if (this.actualizacionSubscription) {
      this.actualizacionSubscription.unsubscribe();
    }
  }

  /**
   * Resetea el estado local del bloqueo. Usado cuando el bloqueo se pierde,
   * la actualización falla, o se libera explícitamente.
   * @param mensajeError Mensaje de error opcional para emitir.
   * @param moduloAfectado El código del módulo que se está liberando/perdiendo.
   * @param liberacionExitosa Indica si la liberación fue confirmada como exitosa (para no mostrar error).
   */
  private forzarLiberacionLocal(mensajeError?: string, moduloAfectado?: number | null, liberacionExitosa: boolean = false) {
    this.log('FORZANDO LIBERACION LOCAL', moduloAfectado, this.codigoModuloBloqueado);
    if (moduloAfectado === null || moduloAfectado === undefined || this.codigoModuloBloqueado === moduloAfectado) {
      this.detenerActualizacionPeriodica();
      this.codigoModuloBloqueado = null;

      if (liberacionExitosa) {
        this.log('LIBERACION LOCAL EXITOSA');
        this.estadoBloqueoActual.next(null);
      } else {
        this.log('LIBERACION LOCAL NO EXITOSA');
        this.estadoBloqueoActual.next({
          exito: false,
          mensajeError: mensajeError || 'El bloqueo se ha perdido o no es válido.',
          codigo_modulo: moduloAfectado || 0,
          rut_usuario: this.rutUsuario,
          token_refresco: this.obtenerTokenRefresco(),
          fecha_expiracion: new Date().toISOString(),
          fecha_ultima_actividad: new Date().toISOString(),
        } as BloqueoModulo);
      }
    }
  }

  private obtenerTokenRefresco(): string {
    return this.securityService.getTokenRefresh();
  }
}
